import { useEffect, useRef, useCallback, useMemo, type RefObject } from "react"
import type {
  Table,
  ColumnSizingState,
  VisibilityState,
} from "@tanstack/react-table"

type PersistedTableState = {
  columnSizing?: ColumnSizingState
  columnOrder?: string[]
  columnVisibility?: VisibilityState
  userColumnVisibility?: VisibilityState
}

const STORAGE_PREFIX = "table-state:"
const PERSIST_DEBOUNCE_MS = 500

function readState(tableId: string): PersistedTableState | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + tableId)
    if (!raw) return null
    return JSON.parse(raw) as PersistedTableState
  } catch {
    return null
  }
}

function writeState(tableId: string, state: PersistedTableState) {
  try {
    localStorage.setItem(STORAGE_PREFIX + tableId, JSON.stringify(state))
  } catch {
    // Silently fail if storage is full or unavailable
  }
}

function removeState(tableId: string) {
  try {
    localStorage.removeItem(STORAGE_PREFIX + tableId)
  } catch {
    // Silently fail
  }
}

/**
 * Reconciles a persisted column order with the current column IDs.
 * Removes columns that no longer exist, appends new ones at the end.
 */
function reconcileColumnOrder(
  persisted: string[],
  current: string[],
): string[] {
  const currentSet = new Set(current)
  const ordered = persisted.filter((id) => currentSet.has(id))
  const orderedSet = new Set(ordered)
  for (const id of current) {
    if (!orderedSet.has(id)) ordered.push(id)
  }
  return ordered
}

/**
 * Returns `value` unchanged until its contents change.
 *
 * State derived from a table's column array is rebuilt whenever the caller
 * rebuilds that array — which some tables do inline, on every render.
 * Downstream the state is compared by identity (the persisted-state read,
 * the view-mode sync), where a new-but-identical object means a localStorage
 * read per render and a visibility write that re-triggers itself. Anchoring
 * identity to the value keeps those comparisons honest.
 */
export function useStableValue<T>(value: T): T {
  const key = JSON.stringify(value)
  const ref = useRef({ key, value })
  if (ref.current.key !== key) ref.current = { key, value }
  return ref.current.value
}

type UsePersistedInitialStateInput = {
  tableId?: string
  defaultColumnSizing: ColumnSizingState
  defaultColumnVisibility?: VisibilityState
  columnIds: string[]
}

/**
 * Reads persisted table state from localStorage on mount.
 * Returns merged initial state values for useReactTable.
 *
 * When `userColumnVisibility` is present in storage, effective visibility
 * is computed as `{ ...modeDefaults, ...userOverrides }` so that
 * user-explicit changes survive view-mode switches.
 */
export function usePersistedInitialState(input: UsePersistedInitialStateInput) {
  const { tableId, defaultColumnSizing, defaultColumnVisibility, columnIds } =
    input

  return useMemo(() => {
    if (!tableId) {
      return {
        columnSizing: defaultColumnSizing,
        columnOrder: [] as string[],
        columnVisibility: (defaultColumnVisibility ?? {}) as VisibilityState,
        userColumnVisibility: {} as VisibilityState,
      }
    }

    const persisted = readState(tableId)
    const userColumnVisibility = persisted?.userColumnVisibility ?? {}
    const hasUserVis = !!persisted?.userColumnVisibility

    return {
      columnSizing: persisted?.columnSizing
        ? { ...defaultColumnSizing, ...persisted.columnSizing }
        : defaultColumnSizing,
      // An EMPTY persisted order means "no explicit order — follow the
      // column definitions". It must stay [] here: reconciling [] would
      // materialize the current definition order into explicit state, get
      // persisted on the next write, and permanently pin the column order
      // as of the user's second visit — silently ignoring any reordering
      // of the column definitions shipped later.
      columnOrder: persisted?.columnOrder?.length
        ? reconcileColumnOrder(persisted.columnOrder, columnIds)
        : [],
      columnVisibility: hasUserVis
        ? ({
            ...defaultColumnVisibility,
            ...userColumnVisibility,
          } as VisibilityState)
        : persisted?.columnVisibility
          ? {
              ...defaultColumnVisibility,
              ...persisted.columnVisibility,
            }
          : ((defaultColumnVisibility ?? {}) as VisibilityState),
      userColumnVisibility,
    }
  }, [tableId, defaultColumnVisibility])
}

type UseTableStatePersistenceInput = {
  tableId?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  table: Table<any>
  defaultColumnSizing: ColumnSizingState
  defaultColumnVisibility: VisibilityState
  userColumnVisibilityRef: RefObject<VisibilityState>
}

/**
 * Observes TanStack Table state and persists column sizing, order,
 * and visibility to localStorage with debouncing.
 *
 * Skips writes while a column is being resized to avoid excess I/O.
 */
export function useTableStatePersistence(input: UseTableStatePersistenceInput) {
  const {
    tableId,
    table,
    defaultColumnSizing,
    defaultColumnVisibility,
    userColumnVisibilityRef,
  } = input
  const defaultSizingRef = useRef(defaultColumnSizing)
  const defaultVisibilityRef = useRef(defaultColumnVisibility)
  defaultVisibilityRef.current = defaultColumnVisibility
  const skipPersistRef = useRef(false)
  // eslint-disable-next-line unicorn/no-useless-undefined
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const { columnSizing, columnOrder, columnVisibility, columnSizingInfo } =
    table.getState()
  const isResizing = !!columnSizingInfo.isResizingColumn

  useEffect(() => {
    if (!tableId || isResizing) return
    if (skipPersistRef.current) {
      skipPersistRef.current = false
      return
    }

    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      writeState(tableId, {
        columnSizing,
        columnOrder,
        columnVisibility,
        userColumnVisibility: userColumnVisibilityRef.current,
      })
    }, PERSIST_DEBOUNCE_MS)

    return () => clearTimeout(timerRef.current)
  }, [
    tableId,
    columnSizing,
    columnOrder,
    columnVisibility,
    isResizing,
    userColumnVisibilityRef,
  ])

  useEffect(() => {
    return () => clearTimeout(timerRef.current)
  }, [])

  const resetState = useCallback(() => {
    if (!tableId) return
    removeState(tableId)
    skipPersistRef.current = true
    userColumnVisibilityRef.current = {}
    table.setColumnSizing(defaultSizingRef.current)
    table.setColumnOrder([])
    table.setColumnVisibility(defaultVisibilityRef.current)
  }, [tableId, table, userColumnVisibilityRef])

  return { resetState }
}

/**
 * Reorders a column in the table by moving it from its current position
 * to before or after a target column.
 */
export function reorderColumn(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  table: Table<any>,
  sourceId: string,
  targetId: string,
  insertAfter: boolean,
) {
  const order = table.getAllLeafColumns().map((c) => c.id)
  const sourceIdx = order.indexOf(sourceId)
  const targetIdx = order.indexOf(targetId)
  if (sourceIdx === -1 || targetIdx === -1 || sourceIdx === targetIdx) return

  const newOrder = [...order]
  newOrder.splice(sourceIdx, 1)

  const adjustedTargetIdx = newOrder.indexOf(targetId)
  const insertIdx = insertAfter ? adjustedTargetIdx + 1 : adjustedTargetIdx
  newOrder.splice(insertIdx, 0, sourceId)

  table.setColumnOrder(newOrder)
}
